import React, { Component } from 'react'
import {
    Form,
    Card,
    InputNumber,
    Tag,
    Divider,
    Switch,
    Select,
    message
} from 'antd'
import styled from 'styled-components'
import { ReadOutlined } from '@ant-design/icons'
import { getAppConfig, setAppConfig } from '../../../services/apiServices'
import RowLayout from '../../../commons/RowLayout/RowLayout'
import LoadingModal from '../../../commons/Modal/Loading'
import SaveButton from '../../../commons/Button/SaveButton'

const { Option, OptGroup } = Select

const COVER_ASPECT_RATIO = {
    '1:1': { width: 1, height: 1 },
    '3:2': { width: 3, height: 2 },
    '4:3': { width: 4, height: 3 },
    '16:9': { width: 16, height: 9 },
    '2:3': { width: 2, height: 3 },
    '3:4': { width: 3, height: 4 },
    '9:16': { width: 9, height: 16 }
}

const COVER_ASPECT_RATIO_GROUPS = [
    { name: 'SQUARE', options: ['1:1'] },
    { name: 'LANDSCAPE', options: ['3:2', '4:3', '16:9'] },
    { name: 'PROTRAIT', options: ['2:3', '3:4', '9:16'] }
]

const SOCIAL_SHARE_APPS = [
    { key: 'pinterest', name: 'Pinterest' },
    { key: 'facebook', name: 'Facebook' },
    { key: 'twitter', name: 'X (Twitter)' },
    { key: 'line', name: 'Line' },
    { key: 'linkedin', name: 'LinkedIn' },
    { key: 'tumblr', name: 'Tumblr' }
]

const Container = styled(Card)`
    .image-wrapper {
        display: inline-block;
        padding: 20px;
        margin-top: 15px;
        border: 1px dashed #ccc;
        > img {
            max-width: 400px;
        }
    }
    .header-image-wrapper {
        cursor: pointer;
        display: flex;
        max-width: 500px;
        margin-bottom: 15px;
        &:hover {
            opacity: 0.8;
        }
        &.selected {
            border: 3px solid #1890ff;
        }

        img {
            width: 100%;
        }
    }
    .radio-y {
        display: block;
        height: 30px;
        line-height: 30px;
    }
`

class ArticleGeneral extends Component {
    state = {
        isLoading: false
    }

    formRef = React.createRef()

    componentDidMount() {
        this.getConfig()
    }

    onFinish(values) {
        let data = {
            post_card_aspect_ratio: this.getAspectRatioValue(
                values.postCardAspectRatio
            ),
            enable_post_card_border: values.enablePostCardBorder,
            enable_post_card_category_tags: values.enablePostCardCategoryTags,
            enable_single_post_related: values.enableSinglePostRelated,
            enable_single_post_comment: values.enableSinglePostComment,
            posts_per_page: values.postsPerPage,
            posts_per_row: {
                xs: values.postsPerRowXs,
                md: values.postsPerRowMd,
                lg: values.postsPerRowLg
            },
            single_post_share_apps: values.singlePostShareApps
        }
        this.setConfig(data)
    }

    async getConfig() {
        this.setState({ isLoading: true })
        try {
            let config = await getAppConfig()
            this.setState({
                isLoading: false
            })

            this.formRef.current &&
                this.formRef.current.setFieldsValue({
                    postCardAspectRatio: this.generateAspectRatioKey(
                        config.post_card_aspect_ratio
                    ),
                    enablePostCardBorder: !!config.enable_post_card_border,
                    enablePostCardCategoryTags:
                        !!config.enable_post_card_category_tags,
                    enableSinglePostRelated:
                        !!config.enable_single_post_related,
                    enableSinglePostComment:
                        !!config.enable_single_post_comment,
                    postsPerPage: config.posts_per_page,
                    postsPerRowXs: config.posts_per_row?.xs || 3,
                    postsPerRowMd: config.posts_per_row?.md || 2,
                    postsPerRowLg: config.posts_per_row?.lg || 2,
                    singlePostShareApps: config.single_post_share_apps || []
                })
        } catch (error) {
            console.log(error)
            this.setState({ isLoading: false })
            message.error(this.props.T('SOMETHING_WRONG'))
        }
    }

    async setConfig(data) {
        this.setState({ isLoading: true })
        try {
            await setAppConfig({
                body: { config: data }
            })
            this.setState({
                isLoading: false
            })
            message.success(this.props.T('SAVED'))
        } catch (error) {
            this.setState({ isLoading: false })
            message.error(this.props.T('SOMETHING_WRONG'))
        }
    }

    generateAspectRatioKey(ratio = {}) {
        if (!ratio.width || !ratio.height) {
            ratio.width = 4
            ratio.height = 3
        }
        let key = ratio.width + ':' + ratio.height
        return key
    }

    getAspectRatioValue(key) {
        let item = COVER_ASPECT_RATIO[key]
        let ratio = { width: 4, height: 3 }
        if (item) {
            let { width, height } = item
            ratio = { width, height }
        }
        return ratio
    }

    onChangeSinglePostImageStyle(style) {
        this.setState({ singlePostImageStyle: style })
    }

    onChangeSinglePostDetailStyle(style) {
        this.setState({ singlePostDetailStyle: style })
    }

    onChangeColor = color => {
        this.setState({ starColor: color })
    }

    render() {
        let { T } = this.props

        return (
            <Form ref={this.formRef} onFinish={this.onFinish.bind(this)}>
                <Container
                    title={
                        <span className="text-xl">
                            <ReadOutlined /> {T('ARTICLE')} - {T('GENERAL')}
                        </span>
                    }>
                    <Divider orientation="left">{T('CATEGORY_PAGE')}</Divider>

                    <RowLayout title={T('ENABLE_POST_CARD_BORDER')}>
                        <Form.Item
                            name="enablePostCardBorder"
                            valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </RowLayout>

                    <RowLayout title={T('ENABLE_POST_CARD_CATEGORY_TAG')}>
                        <Form.Item
                            name="enablePostCardCategoryTags"
                            valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </RowLayout>

                    <RowLayout title={T('COVER_ASPECT_RATIO')}>
                        <Form.Item name="postCardAspectRatio">
                            <Select style={{ width: 88 }}>
                                {COVER_ASPECT_RATIO_GROUPS.map(group => (
                                    <OptGroup
                                        key={group.name}
                                        label={T(group.name)}>
                                        {group.options.map(key => (
                                            <Option key={key} value={key}>
                                                {COVER_ASPECT_RATIO[key].width}{' '}
                                                :{' '}
                                                {COVER_ASPECT_RATIO[key].height}
                                            </Option>
                                        ))}
                                    </OptGroup>
                                ))}
                            </Select>
                        </Form.Item>
                    </RowLayout>

                    <RowLayout title={T('NUMBER_POSTS_TOTAL')}>
                        <Form.Item
                            name="postsPerPage"
                            rules={[
                                {
                                    required: true,
                                    message: T('FORM_ERR_REQUIRE')
                                }
                            ]}>
                            <InputNumber min={1} />
                        </Form.Item>
                    </RowLayout>

                    <RowLayout title={T('NUMBER_POSTS_ROW')}>
                        <div
                            style={{ display: 'flex', alignItems: 'baseline' }}>
                            <Form.Item
                                name="postsPerRowLg"
                                rules={[
                                    {
                                        required: true,
                                        message: T('FORM_ERR_REQUIRE')
                                    }
                                ]}>
                                <InputNumber
                                    min={1}
                                    max={12}
                                    style={{
                                        display: 'inline-block',
                                        marginRight: 10
                                    }}
                                />
                            </Form.Item>
                            <span>
                                <Tag color="magenta">{T('ON_DESKTOP')}</Tag>
                            </span>
                        </div>
                        <div
                            style={{ display: 'flex', alignItems: 'baseline' }}>
                            <Form.Item
                                name="postsPerRowMd"
                                rules={[
                                    {
                                        required: true,
                                        message: T('FORM_ERR_REQUIRE')
                                    }
                                ]}>
                                <InputNumber
                                    min={1}
                                    max={12}
                                    style={{
                                        display: 'inline-block',
                                        marginRight: 10
                                    }}
                                />
                            </Form.Item>
                            <span>
                                <Tag color="orange">{T('ON_TABLET')}</Tag>
                            </span>
                        </div>
                        <div
                            style={{ display: 'flex', alignItems: 'baseline' }}>
                            <Form.Item
                                name="postsPerRowXs"
                                rules={[
                                    {
                                        required: true,
                                        message: T('FORM_ERR_REQUIRE')
                                    }
                                ]}>
                                <InputNumber
                                    min={1}
                                    max={12}
                                    style={{
                                        display: 'inline-block',
                                        marginRight: 10
                                    }}
                                />
                            </Form.Item>
                            <span>
                                <Tag color="green">{T('ON_MOBILE')}</Tag>
                            </span>
                        </div>
                    </RowLayout>

                    <Divider orientation="left">{T('ARTICLE_PAGE')}</Divider>

                    <RowLayout title={T('ENABLE_COMMENT_BOX')}>
                        <Form.Item
                            name="enableSinglePostComment"
                            valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </RowLayout>

                    <RowLayout title={T('ENABLE_RELATED_POSTS')}>
                        <Form.Item
                            name="enableSinglePostRelated"
                            valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </RowLayout>

                    <RowLayout title={T('SHARING_CHANNEL')}>
                        <Form.Item name="singlePostShareApps">
                            <Select
                                mode="multiple"
                                placeholder={T('SELECT_CHANNELS')}
                                optionLabelProp="label">
                                {SOCIAL_SHARE_APPS.map(app => (
                                    <Option
                                        key={app.key}
                                        value={app.key}
                                        label={app.name}>
                                        {app.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </RowLayout>

                    <LoadingModal isOpen={this.state.isLoading} />
                </Container>
                <SaveButton T={T} />
            </Form>
        )
    }
}

export default ArticleGeneral
